/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../../utils/logger.js";
/**
 * @fileoverview Location section for appointment registration: second address (domicilio2)
 * with country, state, and town selects. Uses reference data from parent when provided
 * to avoid refetching on profile change; otherwise fetches countries/states/towns once.
 * @module components/AppointmentRegistrationSections/Location
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./Location.module.css";
import { useTranslation } from "react-i18next";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import { getReferenceData } from "../../../logic/companyEdit";
import { useNavigate } from "react-router";
import Select from "react-select";
import {
  getReferencedDataOptionById,
  getSelectStylesWithError,
  selectStyles,
} from "../../../utils/common";

/**
 * Renders the second address (commercial) form: country, state, town.
 * Reference data (countries, states, towns) is used from parent when passed to avoid refetch on remount.
 *
 * @param {Object} props
 * @param {Object} [props.company] - Company with direccion[1] (segundo domicilio: pais, estado, municipio)
 * @param {function(string, *): void} [props.onFieldChange] - Called with (fieldName, value) on change
 * @param {function(boolean): void} [props.onValidationChange] - Called with hasErrors
 * @param {Object} [props.missingRequiredFields] - Map of field key to boolean (true = missing)
 * @param {function(string): boolean} [props.getFieldValidation] - Returns true when a required field is missing
 * @param {{ countries: Array, states: Array, towns: Array }|null} [props.referenceData] - Pre-fetched reference data from parent; when provided, no API call is made
 * @returns {JSX.Element}
 */
const Location = ({
  company,
  onFieldChange,
  onValidationChange,
  missingRequiredFields,
  getFieldValidation,
  referenceData: referenceDataFromParent,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevHasErrorsRef = useRef(false);

  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;
  const isNotComputer = isTablet || isMediumMobile || isSmallMobile;

  const getNotSingleSelectWidth = () => {
    return "100%";
  };

  const secondAddressCountryRef = useRef(null);
  const secondAddressStateRef = useRef(null);
  const secondAddressTownRef = useRef(null);

  const [secondAddressCountries, setSecondAddressCountries] = useState([]);
  const [secondAddressStates, setSecondAddressStates] = useState([]);
  const [generalTowns, setGeneralTowns] = useState([]);
  const [secondAddressTowns, setSecondAddressTowns] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /** Fetches countries, states, and towns from the reference data API when not provided by parent. */
  const fetchReferenceData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [countriesData, statesData, townsData] = await Promise.all([
        getReferenceData("countries", navigate),
        getReferenceData("states", navigate),
        getReferenceData("towns", navigate),
      ]);

      setSecondAddressCountries(
        countriesData && countriesData.length > 0 ? countriesData : [],
      );
      setSecondAddressStates(
        statesData && statesData.length > 0 ? statesData : [],
      );
      setGeneralTowns(townsData && townsData.length > 0 ? townsData : []);
      setSecondAddressTowns(townsData && townsData.length > 0 ? townsData : []);
    } catch (err) {
      logger.error("Error fetching reference data:", {
        err: sanitizeError(err),
      });
      setError(t("errors.loading.referenceDataFailed"));
      setSecondAddressCountries([]);
      setSecondAddressStates([]);
      setSecondAddressTowns([]);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  /** Normalizes option value for react-select (null/undefined → null). */
  const getOptionValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    return value;
  };

  /** Returns the selected option object from options by value, or null. */
  const getSafeSelectedOption = (options, value) => {
    if (!Array.isArray(options)) {
      logger.warn("Options is not an array:", { data: options });
      return null;
    }
    return getReferencedDataOptionById(options, getOptionValue(value));
  };

  // Use reference data from parent when provided (avoids refetch on every profile change); otherwise fetch once
  useEffect(() => {
    if (
      referenceDataFromParent &&
      referenceDataFromParent.countries &&
      referenceDataFromParent.countries.length > 0
    ) {
      setSecondAddressCountries(referenceDataFromParent.countries);
      setSecondAddressStates(referenceDataFromParent.states || []);
      setGeneralTowns(referenceDataFromParent.towns || []);
      setSecondAddressTowns(referenceDataFromParent.towns || []);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (referenceDataFromParent === undefined) {
      fetchReferenceData();
    } else {
      setIsLoading(true);
    }
  }, [referenceDataFromParent, fetchReferenceData]);

  // Domicilio 2 (Comercial)
  const [segundoDomicilioMunicipio, setSegundoDomicilioMunicipio] = useState(
    company?.domicilio2Municipio || "",
  );
  const [segundoDomicilioEstado, setSegundoDomicilioEstado] = useState(
    company?.domicilio2Estado || "",
  );
  const [segundoDomicilioPais, setSegundoDomicilioPais] = useState(
    company?.domicilio2Pais || "",
  );

  /** Notifies parent of a field change (e.g. domicilio2Pais, domicilio2Estado, domicilio2Municipio). */
  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.location.address2Title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
        {company && Object.hasOwn(company, "domicilio2Pais") && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.location.countryLabel")}
              {missingRequiredFields &&
                Object.hasOwn(missingRequiredFields, "domicilio2Pais") && (
                  <span className={styles.required}> *</span>
                )}
            </label>
            <Select
              ref={secondAddressCountryRef}
              isSearchable={true}
              value={getSafeSelectedOption(
                secondAddressCountries,
                segundoDomicilioPais,
              )}
              onChange={(selectedOption) => {
                logger.info("+++ Second Country selectedOption:", {
                  data: selectedOption,
                });
                if (!selectedOption) {
                  return;
                }
                setSegundoDomicilioPais(selectedOption._id);
                handleInputChange("domicilio2Pais", selectedOption._id);
              }}
              options={secondAddressCountries}
              placeholder={t("components.location.countryPlaceholder")}
              styles={getSelectStylesWithError(
                getFieldValidation && getFieldValidation("domicilio2Pais"),
              )}
              className="select__control"
              getOptionValue={(option) => option._id}
              formatOptionLabel={(option) => <span>{option.nombre}</span>}
            />
          </div>
        )}

        {company && Object.hasOwn(company, "domicilio2Estado") && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.location.stateLabel")}
              {missingRequiredFields &&
                Object.hasOwn(missingRequiredFields, "domicilio2Estado") && (
                  <span className={styles.required}> *</span>
                )}
            </label>
            <Select
              ref={secondAddressStateRef}
              isSearchable={true}
              value={getSafeSelectedOption(
                secondAddressStates,
                segundoDomicilioEstado,
              )}
              onChange={(selectedOption) => {
                logger.info("+++ Second State selectedOption:", {
                  data: selectedOption,
                });
                if (!selectedOption) {
                  return;
                }

                setSegundoDomicilioEstado(selectedOption._id);
                handleInputChange("domicilio2Estado", selectedOption._id);

                // Clear dependent fields
                secondAddressTownRef.current.clearValue();
                setSegundoDomicilioMunicipio("");
                handleInputChange("domicilio2Municipio", "");

                // Filter towns
                const filteredTowns = generalTowns.filter(
                  (town) => town.idEstado == selectedOption._id,
                );
                logger.info("onChange secondState filteredTowns:", {
                  data: filteredTowns,
                });
                setSecondAddressTowns(filteredTowns);
              }}
              options={secondAddressStates}
              placeholder={t("components.location.statePlaceholder")}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: "pointer",
                  borderColor:
                    getFieldValidation && getFieldValidation("domicilio2Estado")
                      ? "#dc3545"
                      : baseStyles.borderColor,
                  marginBottom: "-10px",
                  width: getNotSingleSelectWidth(),
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                indicatorSeparator: () => ({ display: "none" }),
              }}
              className="select__control"
              getOptionValue={(option) => option._id}
              formatOptionLabel={(option) => <span>{option.nombre}</span>}
            />
          </div>
        )}

        {company && Object.hasOwn(company, "domicilio2Municipio") && (
          <div className={`${styles.inputGroup} `}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                {t("components.location.townLabel")}
                {missingRequiredFields &&
                  Object.hasOwn(
                    missingRequiredFields,
                    "domicilio2Municipio",
                  ) && <span className={styles.required}> *</span>}
              </label>
              <Select
                ref={secondAddressTownRef}
                isSearchable={true}
                value={getSafeSelectedOption(
                  secondAddressTowns,
                  segundoDomicilioMunicipio,
                )}
                onChange={(selectedOption) => {
                  logger.info("+++ Second Town selectedOption:", {
                    data: selectedOption,
                  });
                  if (!selectedOption) {
                    return;
                  }

                  setSegundoDomicilioMunicipio(selectedOption._id);
                  handleInputChange("domicilio2Municipio", selectedOption._id);
                }}
                options={secondAddressTowns}
                placeholder={t("components.location.townPlaceholder")}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                    borderColor:
                      getFieldValidation &&
                      getFieldValidation("domicilio2Municipio")
                        ? "#dc3545"
                        : baseStyles.borderColor,
                    marginBottom: "-10px",
                    width: getNotSingleSelectWidth(),
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                className="select__control"
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Location;
